import {
  observable,
  action,
  autorun,
  extendObservable,
  makeObservable,
  computed,
  ObservableMap
} from 'mobx'
import localStore from 'store'
import { isPaused } from '../hooks/use-debounced-props'
import { initToaster } from '../components/Toaster'

class UIStore {
  selectedItem = null
  mouseLabel = null
  hoveredItem = null
  messages = []
  metadata = null
  dataSourceFactory = null
  easyConfigLastOpened = ''
  toastError = null
  shiftKeyUp = false
  isPreview = false
  clipboard = {}
  lastRefreshTime = null
  selectedItemRef = observable.box(null)
  previewUrl = null
  router = null
  selectedMapping = null
  preventCollision = false
  contentRoute = null
  selectedDevice = 'desktop'
  selectedDataSourceId = null
  drawerOpen = false
  localSettings = new Map() as ObservableMap
  componentProps = new Map() as ObservableMap
  editingTheme = null
  isPreviewingAssets = false
  layoutEditorScale = 1
  xRay = false
  constructor () {
    makeObservable(this, {
      localSettings: observable.shallow,
      componentProps: observable,
      selectedItem: observable,
      mouseLabel: observable,
      hoveredItem: observable,
      messages: observable,
      metadata: observable,
      dataSourceFactory: observable,
      easyConfigLastOpened: observable,
      propsPaneSettings: computed,
      toastError: observable,
      shiftKeyUp: observable,
      isPreview: observable,
      previewUrl: observable,
      router: observable,
      selectedMapping: observable,
      preventCollision: observable,
      contentRoute: observable,
      selectedDevice: observable,
      selectedDataSourceId: observable,
      drawerOpen: observable,
      set: action,
      pauseMapper: action,
      propsMapperPaused: computed,
      refreshData: action,
      resumeMapper: action,
      togglePaused: action,
      setRouter: action,
      setSelectedDevice: action,
      setPreviewUrl: action,
      selectItem: action,
      selectItemRef: action,
      addRecentBinding: action,
      togglePreview: action,
      toggleXray: action,
      setDataSourceFactory: action,
      addError: action,
      addInfo: action,
      clearMessages: action,
      updateSelected: action,
      togglePreventCollision: action,
      editTheme: action,
      editingTheme: observable.shallow,
      previewAssets: action,
      isPreviewingAssets: observable,
      layoutEditorScale: observable,
      setScale: action,
      xRay: observable
    })

    if (typeof window !== 'undefined') {
      if ((window as any).is_pls_client) {
        extendObservable(this, {
          lastRefreshTime: null
        })
      }
      try {
        this.localSettings.replace(localStore.get('ph-settings'))
      } catch (e) { }
    }
  }

  set = (k, v) => {
    this[k] = v
  }

  setScale (n) {
    this.layoutEditorScale = n
  }

  get propsPaneSettings () {
    const isExpanded = this.localSettings.get('pane.expanded')
    return {
      isExpanded: isExpanded === true || isExpanded === undefined,
      split: this.localSettings.get('pane.position') === 'bottom'
        ? 'horizontal'
        : 'vertical',
      defaultSelectedTab: this.localSettings.get('pane.defaultSelectedTab') || 0,
      splitRatio: this.localSettings.get('pane.splitRatio') || 0.6
    }
  }

  get propsMapperPaused () {
    return isPaused.get()
  }

  pauseMapper = () => {
    isPaused.set(true)
  }

  refreshData = () => {
    if (!uistore.selectedItem) return
    const dataReload = true
    this.dataSourceFactory.loadData(uistore.selectedItem.completeFieldSet, dataReload)
    this.lastRefreshTime = new Date().getTime()
  }

  resumeMapper = () => {
    isPaused.set(false)
  }

  togglePaused = () => {
    isPaused.set(!isPaused.get())
  }

  setRouter (r) {
    const { match, location, history } = r
    this.router = { match, location, history }
  }

  setSelectedDevice (device) {
    this.selectedDevice = device
  }

  setPreviewUrl (url) {
    this.previewUrl = url
  }

  selectItemRef (ref) {
    this.selectedItemRef.set(ref)
  }

  selectItem (item, ref) {
    this.editingTheme = null
    this.isPreviewingAssets = false
    this.selectedItem = item
    this.selectedItemRef.set(ref)
  }

  get recentBindings () {
    return this.localSettings.get('recentBindings') || []
  }

  addRecentBinding = b => {
    const rb = this.recentBindings || []
    const foundIndex = rb.findIndex(bb => b === bb)
    if (foundIndex !== -1) {
      rb.splice(foundIndex, 1)
    }
    rb.unshift(b)
    rb.length = 25
    this.localSettings.set('recentBindings', rb)
  }

  togglePreview = () => {
    this.isPreview = !this.isPreview
  }

  toggleXray = () => {
    this.xRay = !this.xRay
  }

  setDataSourceFactory (f) {
    this.dataSourceFactory = f
    return this.dataSourceFactory
  }

  addError = (msg: string) => {
    this.messages.push({ message: msg, intent: 3 })
  }

  addInfo = (msg: string) => {
    this.messages.push({ message: msg, intent: 1 })
  }

  clearMessages = () => {
    this.messages.length = 0
  }

  updateSelected = component => {
    this.selectedItem = component
  }

  togglePreventCollision = () => {
    this.preventCollision = !this.preventCollision
  }

  ds = (id: string) => {
    return this.dataSourceFactory && this.dataSourceFactory.getInstance(id)
  }

  editTheme (theme) {
    this.editingTheme = theme
    this.isPreviewingAssets = false
    this.selectedItem = null
  }

  previewAssets () {
    this.editingTheme = null
    this.selectedItem = null
    this.isPreviewingAssets = true;
  }

  get selectedTheme () {
    return this.localSettings.get('theme')
  }
}
export type IUIStore = typeof UIStore

export let uistore: UIStore;
export const initUiStore = () => {
  if (uistore) return uistore
  uistore = new UIStore()
  if (typeof window !== 'undefined') {
    initToaster(uistore);
    (window as any).uistore = uistore
    autorun(() => {
      const newSettings = uistore.localSettings.toJSON()
      if (newSettings) localStore.set('ph-settings', newSettings)
    })
  }
  return uistore
}
